//* Vue
import type { SetupContext } from "vue";
//* 组件库
import { type UiTableOption2, type UiModal, $loading } from "various-ui";
//* 私有组件属性
import type { DrawEmitInfoData } from "@/views/tool/components/sequence/src/types";
//* 组件属性
import { ModuleEmitsOption } from "../index";
//* 公共函数
import { Formats } from "@/assets/js";

export const useComposable = (emits: SetupContext<typeof ModuleEmitsOption>["emit"]) => {
    //* 插件
    const i18n = useI18n();
    const route = useRoute();

    //* 静态属性
    const vars = { option: <DrawEmitInfoData>{} };
    //* 响应式属性
    const refs = {
        data: ref<any[]>([]),
        site: ref<number>(0),
        name: ref<string>(""),
        codon: ref<string>(""),
        visible: ref<boolean>(false),
    };

    //* 节点
    const nodes = {
        container: ref<InstanceType<typeof UiModal>>(),
    };

    //* 函数列表
    const methods = {
        //* 窗口激活函数
        open: (id: string | number, option: DrawEmitInfoData) => {
            //* 数据获取
            $loading.open({ message: i18n.t("UnderLoad") });
            api.frontend("/api/mutations/info", {
                data: { id: id, ids: option.ids, mrna: option.mrna },
                method: "POST",
                finally: () => $loading.close(),
                success: (response) => {
                    //* 数据缓存
                    vars.option = option;
                    //* 数据更新
                    refs.site.value = option.site;
                    refs.name.value = option.name;
                    refs.data.value = response || [];
                    refs.codon.value = option.codon;
                    refs.visible.value = option.data.is;
                    //* 激活窗口
                    nodes.container.value?.openModal();
                },
            });
        },

        //* 跳转函数
        next: (mode: "RNA-Splicer" | "Pathogenicity-Predictor") => emits("next", Object.assign(vars.option.data, { mode: mode })),
        //* 预测函数
        prediction: (data: any) => {
            $loading.open({ message: "" });
            api.frontend("/api/bio/variant/prediction", {
                data: { id: data.task, mode: "RNA-Splicer" },
                method: "POST",
                finally: () => $loading.close(),
                success: (response) => {
                    open(routerResolve({ name: "RnaSplicerResult", params: { id: response } }).href, "_blank");
                },
            });
        },
    };

    //* 计算属性
    const computeds = {
        mode: computed(() => Formats.query(route.query.mode)),
        option: computed<UiTableOption2[]>(() => {
            if (computeds.mode.value == "codon") {
                return [
                    { key: "clinvar", name: "Clinvar ID", align: "center" },
                    { key: "codon", name: i18n.t("Codon"), align: "center" },
                    { key: "mutation_mrna", name: i18n.t("Nucleotide"), align: "singularity" },
                    { key: "mutation_protein", name: i18n.t("ProteinChange"), align: "singularity" },
                    { key: "type", name: i18n.t("Type"), align: "singularity" },
                    { key: "clinical_significance", name: i18n.t("ClinicalSignificance"), align: "center" },
                    { key: "tools", name: i18n.t("AIPredictor"), align: "center" },
                ];
            } else {
                return [
                    { key: "clinvar", name: "Clinvar ID", align: "center" },
                    { key: "mutation_mrna", name: i18n.t("Nucleotide"), align: "singularity" },
                    { key: "mutation_protein", name: i18n.t("ProteinChange"), align: "singularity" },
                    { key: "type", name: i18n.t("Type"), align: "singularity" },
                    { key: "clinical_significance", name: i18n.t("ClinicalSignificance"), align: "center" },
                    { key: "tools", name: i18n.t("AIPredictor"), align: "center" },
                ];
            }
        }),
    };

    return { refs, nodes, methods, computeds };
};
